import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  FileBarChart, Save, Play, Clock, CheckCircle, XCircle, History,
  Mail, Loader2, RefreshCw
} from 'lucide-react';

const REPORT_TYPES = [
  { value: 'inventory', label: 'Inventario General', desc: 'Stock actual, disponibilidad por categoría' },
  { value: 'movements', label: 'Movimientos', desc: 'Salidas, retornos y pendientes del período' },
  { value: 'low_stock', label: 'Stock Bajo', desc: 'Equipos por debajo del umbral configurado' },
  { value: 'maintenance', label: 'Mantenimiento', desc: 'Estado de mantenimientos programados y vencidos' },
  { value: 'reservations', label: 'Reservas', desc: 'Reservas activas y próximas' },
  { value: 'full', label: 'Reporte Completo', desc: 'Incluye todos los reportes anteriores' },
];

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Lunes' },
  { value: 'tue', label: 'Martes' },
  { value: 'wed', label: 'Miércoles' },
  { value: 'thu', label: 'Jueves' },
  { value: 'fri', label: 'Viernes' },
  { value: 'sat', label: 'Sábado' },
  { value: 'sun', label: 'Domingo' },
];

const ScheduledReportsPage = () => {
  const { api } = useAuth();
  const [config, setConfig] = useState({
    enabled: false,
    frequency: 'weekly',
    time_of_day: '08:00',
    day_of_week: 'mon',
    report_types: ['inventory', 'movements', 'low_stock'],
    recipients: [],
    last_run: null
  });
  const [briefingConfig, setBriefingConfig] = useState({
    enabled: true,
    day_of_week: 'mon',
    time_of_day: '07:00',
    include_reservations: true,
    include_maintenance: true,
    include_movements: true,
    include_stats: true,
    extra_recipients: []
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBriefing, setSavingBriefing] = useState(false);
  const [sendingBriefing, setSendingBriefing] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [newBriefingRecipient, setNewBriefingRecipient] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, histRes, briefRes] = await Promise.all([
        api().get('/scheduled-reports/config'),
        api().get('/scheduled-reports/history'),
        api().get('/briefing/config').catch(() => ({ data: {} }))
      ]);
      if (configRes.data && Object.keys(configRes.data).length > 0) {
        setConfig(prev => ({ ...prev, ...configRes.data }));
      }
      if (briefRes.data && Object.keys(briefRes.data).length > 0) {
        setBriefingConfig(prev => ({ ...prev, ...briefRes.data }));
      }
      setHistory(histRes.data || []);
    } catch {
      toast.error('Error cargando configuración');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().put('/scheduled-reports/config', config);
      toast.success('Configuración guardada');
    } catch {
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  };

  const handleGenerateNow = async (reportType) => {
    setGenerating(reportType);
    try {
      const res = await api().post('/scheduled-reports/generate-now', { report_type: reportType });
      if (res.data.status === 'completed') {
        toast.success(`Reporte "${reportType}" generado exitosamente`);
        loadData(); // Refresh history
      } else {
        toast.error(`Error: ${res.data.error}`);
      }
    } catch {
      toast.error('Error al generar reporte');
    } finally { setGenerating(null); }
  };

  const toggleReportType = (type) => {
    setConfig(prev => ({
      ...prev,
      report_types: prev.report_types.includes(type)
        ? prev.report_types.filter(t => t !== type)
        : [...prev.report_types, type]
    }));
  };

  const addRecipient = () => {
    if (!newRecipient || !newRecipient.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }
    setConfig(prev => ({ ...prev, recipients: [...prev.recipients, newRecipient] }));
    setNewRecipient('');
  };

  const removeRecipient = (idx) => {
    setConfig(prev => ({ ...prev, recipients: prev.recipients.filter((_, i) => i !== idx) }));
  };

  const handleSaveBriefing = async () => {
    setSavingBriefing(true);
    try {
      await api().put('/briefing/config', briefingConfig);
      toast.success('Configuración de briefing guardada');
    } catch {
      toast.error('Error al guardar');
    } finally { setSavingBriefing(false); }
  };

  const handleSendBriefingNow = async () => {
    setSendingBriefing(true);
    try {
      await api().post('/briefing/send-now');
      toast.success('Briefing enviado exitosamente');
    } catch {
      toast.error('Error al enviar briefing');
    } finally { setSendingBriefing(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="scheduled-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileBarChart className="h-6 w-6 text-primary" />Reportes Automáticos</h1>
          <p className="text-sm text-muted-foreground">Configura la generación y envío automático de reportes</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-reports-config-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Guardar Configuración
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Programación</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="enabled"
                  checked={config.enabled}
                  onCheckedChange={(v) => setConfig(prev => ({ ...prev, enabled: v }))}
                  data-testid="reports-enabled-checkbox"
                />
                <Label htmlFor="enabled" className="cursor-pointer">Activar reportes automáticos</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frecuencia</Label>
                  <Select value={config.frequency} onValueChange={v => setConfig(prev => ({ ...prev, frequency: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diario</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hora</Label>
                  <Input type="time" value={config.time_of_day} onChange={e => setConfig(prev => ({ ...prev, time_of_day: e.target.value }))} />
                </div>
              </div>

              {config.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Día de la semana</Label>
                  <Select value={config.day_of_week} onValueChange={v => setConfig(prev => ({ ...prev, day_of_week: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mon">Lunes</SelectItem>
                      <SelectItem value="tue">Martes</SelectItem>
                      <SelectItem value="wed">Miércoles</SelectItem>
                      <SelectItem value="thu">Jueves</SelectItem>
                      <SelectItem value="fri">Viernes</SelectItem>
                      <SelectItem value="sat">Sábado</SelectItem>
                      <SelectItem value="sun">Domingo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {config.last_run && (
                <p className="text-xs text-muted-foreground">Última ejecución: {new Date(config.last_run).toLocaleString()}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tipos de Reporte</CardTitle>
              <CardDescription>Selecciona qué reportes generar automáticamente</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {REPORT_TYPES.filter(t => t.value !== 'full').map(t => (
                <div key={t.value} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/30">
                  <Checkbox
                    checked={config.report_types.includes(t.value)}
                    onCheckedChange={() => toggleReportType(t.value)}
                    data-testid={`report-type-${t.value}`}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Mail className="h-4 w-4" />Destinatarios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input value={newRecipient} onChange={e => setNewRecipient(e.target.value)} placeholder="email@ejemplo.com" onKeyDown={e => e.key === 'Enter' && addRecipient()} className="flex-1" data-testid="recipient-input" />
                <Button variant="outline" onClick={addRecipient}>Agregar</Button>
              </div>
              {config.recipients.map((email, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/30 rounded px-3 py-1.5 text-sm">
                  <span>{email}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeRecipient(i)}><XCircle className="h-3 w-3" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Generate & History */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Play className="h-4 w-4" />Generar Ahora</CardTitle>
              <CardDescription>Genera un reporte manualmente</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {REPORT_TYPES.map(t => (
                <div key={t.value} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30">
                  <div>
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.desc}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleGenerateNow(t.value)}
                    disabled={generating === t.value}
                    data-testid={`generate-${t.value}-btn`}
                  >
                    {generating === t.value ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><History className="h-4 w-4" />Historial</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-4">Sin reportes generados</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 20).map((h, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/20 text-sm">
                      <div className="flex items-center gap-2">
                        {h.status === 'completed' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                        <div>
                          <p className="font-medium">{h.report_type}</p>
                          <p className="text-xs text-muted-foreground">{new Date(h.generated_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <Badge variant={h.manual ? 'outline' : 'secondary'} className="text-[10px]">{h.manual ? 'Manual' : 'Auto'}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Weekly Briefing Section */}
      <Card data-testid="briefing-config-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Briefing Semanal
              </CardTitle>
              <CardDescription>Resumen semanal automático por correo con reservas, mantenimiento y movimientos</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSendBriefingNow} 
                disabled={sendingBriefing}
                data-testid="send-briefing-now-btn"
              >
                {sendingBriefing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                Enviar Ahora
              </Button>
              <Button 
                size="sm" 
                onClick={handleSaveBriefing} 
                disabled={savingBriefing}
                data-testid="save-briefing-config-btn"
              >
                {savingBriefing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Guardar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="briefing-enabled"
              checked={briefingConfig.enabled}
              onCheckedChange={(v) => setBriefingConfig(prev => ({ ...prev, enabled: v }))}
            />
            <Label htmlFor="briefing-enabled">Activar briefing semanal</Label>
          </div>
          
          {briefingConfig.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Día de envío</Label>
                  <Select value={briefingConfig.day_of_week} onValueChange={v => setBriefingConfig(prev => ({ ...prev, day_of_week: v }))}>
                    <SelectTrigger data-testid="briefing-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hora de envío</Label>
                  <Input 
                    type="time" 
                    value={briefingConfig.time_of_day} 
                    onChange={e => setBriefingConfig(prev => ({ ...prev, time_of_day: e.target.value }))}
                    data-testid="briefing-time-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Incluir en el briefing</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'include_reservations', label: 'Reservas de la semana' },
                    { key: 'include_maintenance', label: 'Mantenimiento vencido' },
                    { key: 'include_movements', label: 'Movimientos recientes' },
                    { key: 'include_stats', label: 'Estadísticas generales' },
                  ].map(opt => (
                    <div key={opt.key} className="flex items-center space-x-2">
                      <Checkbox 
                        id={opt.key}
                        checked={briefingConfig[opt.key]}
                        onCheckedChange={v => setBriefingConfig(prev => ({ ...prev, [opt.key]: v }))}
                      />
                      <Label htmlFor={opt.key} className="text-sm">{opt.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Destinatarios adicionales (además de admins)</Label>
                <div className="flex gap-2">
                  <Input 
                    type="email" 
                    placeholder="correo@ejemplo.com" 
                    value={newBriefingRecipient} 
                    onChange={e => setNewBriefingRecipient(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newBriefingRecipient.includes('@')) { setBriefingConfig(prev => ({ ...prev, extra_recipients: [...(prev.extra_recipients || []), newBriefingRecipient] })); setNewBriefingRecipient(''); } }}}
                    className="flex-1"
                  />
                  <Button variant="outline" size="sm" onClick={() => { 
                    if (newBriefingRecipient.includes('@')) { 
                      setBriefingConfig(prev => ({ ...prev, extra_recipients: [...(prev.extra_recipients || []), newBriefingRecipient] })); 
                      setNewBriefingRecipient(''); 
                    } else { toast.error('Email inválido'); }
                  }}>Agregar</Button>
                </div>
                {briefingConfig.extra_recipients?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {briefingConfig.extra_recipients.map((r, i) => (
                      <Badge key={i} variant="secondary" className="gap-1">
                        {r}
                        <button onClick={() => setBriefingConfig(prev => ({ ...prev, extra_recipients: prev.extra_recipients.filter((_, idx) => idx !== i) }))} className="ml-1 hover:text-destructive">×</button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                El briefing se envía automáticamente a todos los usuarios admin/superadmin con correo configurado, más los destinatarios adicionales listados arriba.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduledReportsPage;
